import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers } from '../services/userService';
import { ComparisonItem, compareShifts } from '../services/shiftService';
import ShiftTimeline, { SERIES } from './ShiftTimeline';
import { toTimelineByDate } from '../utils/splitAcrossDays';

/**
 * 予定と実績 — 組んだシフトと打刻の差。**読むための画面**。
 *
 * ## シフト作成とは分ける（代表判断 2026-08-29）
 *
 * 同じ画面にすると **希望・予定・実績の3本**が並んでごちゃつく。
 * 単位も違う — 作成は「受付期間ごと・未来」、こちらは「月ごと・過去」。
 * 帯の描画だけ `ShiftTimeline` で共通にしている。
 *
 * ## これが上位プランの本体
 *
 * 専業のシフトツールは勤怠と CSV でつながっている。つまり突合がリアルタイムではない。
 * 当社は同じデータベースに打刻があるので、ここを標準の画面として出せる。
 *
 * ## ⚠️ 評価の言葉を出さない
 *
 * 「遅刻」「無断欠勤」とは書かない。**予定と実績の差という事実だけ**を出す。
 * 遅刻・早退の判定は雇用区分・給与計算とセットの別論点。
 */


const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const ShiftComparisonPage: React.FC = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => ymd(new Date()).slice(0, 7));
  const [items, setItems] = useState<ComparisonItem[]>([]);
  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planBlocked, setPlanBlocked] = useState(false);

  const range = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
  }, [month]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [u, c] = await Promise.all([
        getUsers('all').catch(() => [] as any),
        compareShifts(range.from, range.to),
      ]);
      setUsers(u as any);
      setItems(c.items);
    } catch (e: any) {
      if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
      else setError(e.response?.data?.error || '突合に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const nameOf = (id: number) => users.find((u) => u.id === id)?.name ?? `ID ${id}`;

  /*
   * 絞り込みは画面ではやらない（代表指摘 2026-08-29）。
   *
   * ⚠️ ただし**サーバが「公休で打刻も下書きも無い日」を落としている**
   * （代表判断 2026-09-02）。公開時に公休を自動で埋めるようになり、
   * 落とさないとシフト制の人の全日が並ぶため（10人なら約310行）。
   */
  const shown = items;

  /*
   * 日ごとに束ねる。軸を 0:00–24:00 に固定したので、**日をまたぐ勤務は
   * 2日に分けて描く**必要がある（22:00–06:00 → その日の 22-24 と翌日の 0-6）。
   * 翌日側は突合の行が無いこともあるため、セグメントを日付へ配る形で作る。
   */
  const byDate = useMemo(
    () => toTimelineByDate(shown, new Set(shown.map((i) => i.date))),
    [shown]
  );

  if (planBlocked) {
    return (
      <div style={s.page}>
        <div style={s.planCard}>
          <h1 style={s.h1}>予定と実績</h1>
          <p style={s.note}>
            シフト管理は<strong>シフト管理付きプラン</strong>でご利用いただけます。
          </p>
          <button onClick={() => navigate('/subscription')} style={s.primaryBtn}>プランを見る</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>予定と実績</h1>
          <p style={s.sub}>組んだシフトと、実際の打刻の差を見ます</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={s.input} />
          <button onClick={() => navigate('/shifts')} style={s.backBtn}>シフトを組む</button>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn}>戻る</button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {loading ? (
        <p style={s.note}>読み込み中…</p>
      ) : shown.length === 0 ? (
        <div style={s.card}>
          <p style={s.note}>この月のシフトと打刻はまだありません。</p>
        </div>
      ) : (
        /*
          日ごとに区切って、同じ時間軸に予定と実績を並べる。
          数字の表よりズレが速く読める（代表指摘 2026-08-29）。
        */
        <div style={{ display: 'grid', gap: 14 }}>
          {byDate.map(([date, list]) => (
            <section key={date} style={s.card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 15 }}>
                {date}（{WEEK[new Date(`${date}T00:00:00`).getDay()]}）
                <span style={{ fontSize: 12, color: '#64748B', fontWeight: 400, marginLeft: 10 }}>
                  {list.length}人
                </span>
              </h2>
              <ShiftTimeline
                rows={list}
                nameOf={nameOf}
                upper={SERIES.plan}
                lower={SERIES.actual}
                emptyText="この日は予定も打刻もありません。"
              />
            </section>
          ))}
        </div>
      )}

    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  h1: { margin: 0, fontSize: 'clamp(18px, 4vw, 24px)' },
  sub: { margin: '4px 0 0', color: '#6c757d', fontSize: 13 },
  card: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 },
  planCard: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 32, textAlign: 'center', maxWidth: 520, margin: '40px auto' },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.7, margin: 0 },
  error: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 4, padding: 10, marginBottom: 12 },
  input: { padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 },
  primaryBtn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 4, padding: '9px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  backBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #dee2e6', color: '#6c757d', fontSize: 12, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', whiteSpace: 'nowrap' },
  badge: { borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700 },
};

export default ShiftComparisonPage;
